/** Voice recording with MediaRecorder. Safari records MP4/AAC, Chrome and Firefox WebM/Opus (or MP4). */
export interface VoiceClip {
  blob: Blob;
  durationMs: number;
  mimeType: string;
  extension: string;
}

export interface Recording {
  startedAt: number;
  /** Current input loudness, 0..1 (drives the pulsing halo). */
  level(): number;
  stop(): Promise<VoiceClip>;
  cancel(): void;
}

// MP4/AAC first: it plays everywhere, including iPhones.
const CANDIDATES = ['audio/mp4', 'audio/mp4;codecs=mp4a.40.2', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];

export const canRecordVoice = () => typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

export async function startRecording(): Promise<Recording> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });
  const mimeType = CANDIDATES.find((t) => MediaRecorder.isTypeSupported?.(t));
  const recorder = new MediaRecorder(stream, mimeType ? { mimeType, audioBitsPerSecond: 64000 } : undefined);
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
  const startedAt = Date.now();
  recorder.start(1000);
  // Loudness meter. Optional: if WebAudio is unavailable the halo simply does not pulse.
  let meter: { ctx: AudioContext; analyser: AnalyserNode; buf: Uint8Array<ArrayBuffer> } | null = null;
  try {
    const ctx = new AudioContext();
    void ctx.resume();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    ctx.createMediaStreamSource(stream).connect(analyser);
    meter = { ctx, analyser, buf: new Uint8Array(new ArrayBuffer(analyser.fftSize)) };
  } catch {
    meter = null;
  }
  const release = () => {
    stream.getTracks().forEach((t) => t.stop());
    void meter?.ctx.close().catch(() => {});
  };

  return {
    startedAt,
    level: () => {
      if (!meter) return 0;
      meter.analyser.getByteTimeDomainData(meter.buf);
      let sum = 0;
      for (const v of meter.buf) sum += ((v - 128) / 128) ** 2;
      return Math.min(1, Math.sqrt(sum / meter.buf.length) * 5);
    },
    stop: () =>
      new Promise<VoiceClip>((resolve, reject) => {
        recorder.onstop = () => {
          release();
          const type = (recorder.mimeType || mimeType || 'audio/webm').split(';')[0];
          resolve({
            blob: new Blob(chunks, { type }),
            durationMs: Date.now() - startedAt,
            mimeType: type,
            extension: type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm',
          });
        };
        recorder.onerror = () => {
          release();
          reject(new Error('recording_failed'));
        };
        recorder.stop();
      }),
    cancel: () => {
      recorder.onstop = release;
      if (recorder.state !== 'inactive') recorder.stop();
      else release();
    },
  };
}
